import { HIGHLIGHT_FILL_HANDLE_CLASS } from '@databases/highlights/constants';

import { type GridCoord, type GridPosition } from '../../../machine/state/types';

export type ClickTarget =
  | { type: 'cell'; position: GridPosition }
  | { type: 'row'; row: GridCoord; source: 'row-handle' | 'row-checkbox' }
  | { type: 'column'; col: GridCoord }
  | { type: 'headerCheckbox' }
  | { type: 'fillHandle' }
  | { type: 'outside' }
  | { type: 'none' };

const HEADER_CHECKBOX_SELECTOR = '#selections-header-checkbox';
const ROW_BUTTON_SELECTOR = '[data-testid="row-select-button"]';
const ROW_CHECKBOX_SELECTOR = '.selections-entry-checkbox';
const FILL_HANDLE_SELECTOR = `.${HIGHLIGHT_FILL_HANDLE_CLASS}`;

export function getClickTarget(x: number, y: number): ClickTarget {
  const el = document.elementFromPoint(x, y);

  if (!el) {
    return { type: 'none' };
  }

  const headerCheckbox = el.closest(HEADER_CHECKBOX_SELECTOR);
  if (headerCheckbox) {
    return { type: 'headerCheckbox' };
  }

  const rowSelectButtonEl = el.closest(ROW_BUTTON_SELECTOR);
  if (rowSelectButtonEl) {
    const rowEl = rowSelectButtonEl.closest('tr[data-row-index]');

    if (rowEl) {
      const rowIndex = rowEl.getAttribute('data-row-index');

      if (rowIndex !== null) {
        return {
          type: 'row',
          row: {
            index: parseInt(rowIndex, 10),
          },
          source: 'row-handle',
        };
      }
    }
  }

  const rowCheckboxEl = el.closest(ROW_CHECKBOX_SELECTOR);
  if (rowCheckboxEl) {
    const rowIndex = rowCheckboxEl.getAttribute('data-row-index');

    if (rowIndex !== null) {
      return {
        type: 'row',
        row: {
          index: parseInt(rowIndex, 10),
        },
        source: 'row-checkbox',
      };
    }
  }

  const cellEl = el.closest('[data-row-index][data-col-index]');

  if (cellEl) {
    const rowIndex = cellEl.getAttribute('data-row-index');
    const colIndex = cellEl.getAttribute('data-col-index');

    if (rowIndex !== null && colIndex !== null) {
      return {
        type: 'cell',
        position: {
          row: { index: parseInt(rowIndex, 10) },
          col: { index: parseInt(colIndex, 10) },
        },
      };
    }
  }

  const fillHandleEl = el.closest(FILL_HANDLE_SELECTOR);

  if (fillHandleEl) {
    return { type: 'fillHandle' };
  }

  // The "Plain Boring Ancestor" Check
  // We make sure the click is inside the main scroll area...
  const isInsideCanvas = el.closest(
    '#scrollable-inner-container, [data-testid="layout-view--wrapper"], #layout-container'
  );

  // ...but NOT inside the table, and NOT inside a button/input toolbar
  const isInteractive = el.closest(
    'table, button, [role="button"], input, textarea, a, [data-testid="database-value-view-container"], [data-testid="layout-right-side-panel"]'
  );

  if (isInsideCanvas && !isInteractive) {
    return { type: 'outside' };
  }

  return { type: 'none' };
}
